import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import UnitScopeTabs from "../../components/UnitScopeTabs";
import DetalheVendasModal from "../../components/DetalheVendasModal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FileText } from "lucide-react";

const money = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

export default function Performance() {
  const [unidades, setUnidades] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [vendedores, setVendedores] = useState([]);

  const [scope, setScope] = useState("global");
  const [vendedorId, setVendedorId] = useState("");
  const [produtoId, setProdutoId] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [categoria, setCategoria] = useState("");
  const [comDesconto, setComDesconto] = useState(""); // "" | "true" | "false"
  const [descontoMin, setDescontoMin] = useState("");
  const [descontoMax, setDescontoMax] = useState("");

  const [data, setData] = useState(null);
  const [vendas, setVendas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    api.get("/unidades").then((r) => setUnidades(r.data));
    api.get("/produtos").then((r) => setProdutos(r.data));
    api.get("/vendedores").then((r) => setVendedores(r.data));
  }, []);

  const categoriasDisponiveis = useMemo(() => {
    const set = new Set(produtos.map((p) => p.categoria).filter(Boolean));
    return Array.from(set).sort();
  }, [produtos]);

  const vendedoresFiltrados = useMemo(() => {
    if (scope === "global") return vendedores;
    return vendedores.filter((v) => v.unidade_id === scope);
  }, [vendedores, scope]);

  const aplicar = async () => {
    const params = {};
    if (scope !== "global") params.unidade_id = scope;
    if (vendedorId) params.vendedor_id = vendedorId;
    if (produtoId) params.produto_id = produtoId;
    if (dataInicio) params.data_inicio = dataInicio;
    if (dataFim) params.data_fim = dataFim;
    if (categoria) params.categoria = categoria;
    if (comDesconto !== "") params.com_desconto = comDesconto;
    if (descontoMin !== "") params.desconto_min = descontoMin;
    if (descontoMax !== "") params.desconto_max = descontoMax;
    const [r, vs] = await Promise.all([
      api.get("/relatorios/performance", { params }),
      api.get("/vendas", { params: { ...params } }),
    ]);
    setData(r.data);
    const units = unidades.reduce((acc, u) => ({ ...acc, [u.id]: u.nome }), {});
    setVendas(
      vs.data.map((v) => ({ ...v, unidade_nome: units[v.unidade_id] || null })),
    );
  };

  useEffect(() => {
    aplicar();
  }, [scope]); // eslint-disable-line

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Relatórios detalhados
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Performance
        </h2>
      </div>

      <UnitScopeTabs
        unidades={unidades}
        value={scope}
        onChange={(v) => {
          setScope(v);
          setVendedorId("");
        }}
      />

      {data && (
        <div className="flex justify-end mb-4">
          <button
            data-testid="perf-abrir-detalhe"
            onClick={() => setModalOpen(true)}
            className="bg-yellow-400 hover:bg-yellow-500 text-neutral-950 h-10 px-4 font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2 cta-brutal"
          >
            <FileText className="w-4 h-4" /> Relatório detalhado · CSV · Térmico
          </button>
        </div>
      )}

      <div className="bg-white border-2 border-neutral-900 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <F label="Data início">
          <input
            data-testid="perf-data-inicio"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="mt-1 w-full h-10 px-3 border-2 border-neutral-900 font-mono text-sm"
          />
        </F>
        <F label="Data fim">
          <input
            data-testid="perf-data-fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="mt-1 w-full h-10 px-3 border-2 border-neutral-900 font-mono text-sm"
          />
        </F>
        <F label="Vendedor">
          <select
            data-testid="perf-vendedor"
            value={vendedorId}
            onChange={(e) => setVendedorId(e.target.value)}
            className="mt-1 w-full h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
          >
            <option value="">Todos</option>
            {vendedoresFiltrados.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nome}
              </option>
            ))}
          </select>
        </F>
        <F label="Produto (filtro por mercadoria)">
          <select
            data-testid="perf-produto"
            value={produtoId}
            onChange={(e) => setProdutoId(e.target.value)}
            className="mt-1 w-full h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
          >
            <option value="">Todos</option>
            {produtos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
        </F>
        <F label="Categoria">
          <select
            data-testid="perf-categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            className="mt-1 w-full h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
          >
            <option value="">Todas</option>
            {categoriasDisponiveis.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </F>
        <F label="Tem desconto?">
          <select
            data-testid="perf-com-desconto"
            value={comDesconto}
            onChange={(e) => setComDesconto(e.target.value)}
            className="mt-1 w-full h-10 px-2 border-2 border-neutral-900 font-mono text-sm bg-white"
          >
            <option value="">Indiferente</option>
            <option value="true">Apenas com desconto</option>
            <option value="false">Apenas sem desconto</option>
          </select>
        </F>
        <F label="Desconto % (min)">
          <input
            data-testid="perf-desc-min"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={descontoMin}
            onChange={(e) => setDescontoMin(e.target.value)}
            className="mt-1 w-full h-10 px-3 border-2 border-neutral-900 font-mono text-sm"
            placeholder="0"
          />
        </F>
        <F label="Desconto % (max)">
          <input
            data-testid="perf-desc-max"
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={descontoMax}
            onChange={(e) => setDescontoMax(e.target.value)}
            className="mt-1 w-full h-10 px-3 border-2 border-neutral-900 font-mono text-sm"
            placeholder="100"
          />
        </F>
        <button
          data-testid="perf-aplicar"
          onClick={aplicar}
          className="bg-neutral-950 text-white h-10 font-display font-bold uppercase text-xs tracking-widest"
        >
          Aplicar
        </button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <Metric label="Faturamento Bruto" value={money(data.faturamento_bruto)} />
            <Metric label="Descontos" value={money(data.total_descontos)} />
            <Metric label="Fat. Líquido" value={money(data.faturamento_liquido)} />
            <Metric label="CMV" value={money(data.cmv)} />
            <Metric
              label="Lucro Líquido"
              value={money(data.lucro)}
              sub={`Margem ${data.margem_lucro}%`}
              accent="bg-green-600 text-white"
            />
          </div>

          <p className="font-mono text-[11px] text-neutral-500 mb-4">
            * Considera apenas vendas em caixas já fechados.
          </p>

          {/* Análise de Descontos */}
          <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
            <h3 className="font-display font-bold uppercase mb-3">Análise de Descontos</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Stat
                label="Sem desconto"
                value={`${data.analise_descontos.vendas_sem_desconto} vendas`}
                sub={`${data.analise_descontos.perc_sem_desconto}% · ticket ${money(data.analise_descontos.ticket_medio_sem)}`}
              />
              <Stat
                label="Com desconto"
                value={`${data.analise_descontos.vendas_com_desconto} vendas`}
                sub={`${data.analise_descontos.perc_com_desconto}% · ticket ${money(data.analise_descontos.ticket_medio_com)}`}
                accent="bg-yellow-400 text-neutral-950"
              />
              <Stat
                label="Faturamento c/ desc."
                value={money(data.analise_descontos.fat_com_desconto)}
              />
              <Stat
                label="Desconto médio"
                value={`${data.analise_descontos.desconto_medio_pct}%`}
              />
            </div>
            <h4 className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
              Distribuição por faixa de desconto
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 bg-neutral-50">
                    <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Faixa</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Vendas</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">% Total</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Faturamento</th>
                  </tr>
                </thead>
                <tbody>
                  {data.faixas_desconto.map((f) => (
                    <tr
                      key={f.faixa}
                      data-testid={`perf-faixa-desc-${f.faixa.replace(/\W+/g, "_")}`}
                      className="border-b border-neutral-100"
                    >
                      <td className="p-2 font-display font-bold">{f.faixa}</td>
                      <td className="p-2 text-right font-mono">{f.qtd}</td>
                      <td className="p-2 text-right font-mono">{f.perc}%</td>
                      <td className="p-2 text-right font-mono">{money(f.faturamento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Análise de Ticket */}
          <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
            <h3 className="font-display font-bold uppercase mb-3">Análise de Ticket</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Stat label="Ticket médio" value={money(data.ticket_medio)} accent="bg-green-600 text-white" />
              <Stat label="Menor" value={money(data.ticket_min)} />
              <Stat label="Maior" value={money(data.ticket_max)} />
              <Stat label="Total de vendas" value={data.qtd_vendas} />
            </div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={data.faixas_ticket}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="faixa" fontSize={11} />
                  <YAxis fontSize={10} />
                  <Tooltip />
                  <Bar dataKey="qtd" fill="#0A0A0A" name="Vendas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-300 bg-neutral-50">
                    <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Faixa</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Vendas</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">% Total</th>
                    <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Faturamento</th>
                  </tr>
                </thead>
                <tbody>
                  {data.faixas_ticket.map((f) => (
                    <tr
                      key={f.faixa}
                      data-testid={`perf-faixa-ticket-${f.faixa.replace(/\W+/g, "_")}`}
                      className="border-b border-neutral-100"
                    >
                      <td className="p-2 font-display font-bold">{f.faixa}</td>
                      <td className="p-2 text-right font-mono">{f.qtd}</td>
                      <td className="p-2 text-right font-mono">{f.perc}%</td>
                      <td className="p-2 text-right font-mono">{money(f.faturamento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Análise por Categoria */}
          <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
            <h3 className="font-display font-bold uppercase mb-3">
              Vendas por Categoria
            </h3>
            {data.categorias.length === 0 ? (
              <p className="text-sm text-neutral-500">Sem dados.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-300 bg-neutral-50">
                      <th className="text-left p-2 font-mono text-[10px] uppercase tracking-widest">Categoria</th>
                      <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Itens</th>
                      <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">Faturamento</th>
                      <th className="text-right p-2 font-mono text-[10px] uppercase tracking-widest">% Faturamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.categorias.map((c) => (
                      <tr
                        key={c.categoria}
                        data-testid={`perf-cat-${c.categoria.replace(/\W+/g, "_")}`}
                        className="border-b border-neutral-100"
                      >
                        <td className="p-2 font-display font-bold">{c.categoria}</td>
                        <td className="p-2 text-right font-mono">{c.qtd_itens}</td>
                        <td className="p-2 text-right font-mono">{money(c.faturamento)}</td>
                        <td className="p-2 text-right font-mono">{c.perc_faturamento}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data.filtro_produto && (
            <div className="bg-white border-2 border-neutral-900 p-5 mb-6">
              <h3 className="font-display font-bold uppercase mb-3">
                Vendas do produto selecionado por vendedor
              </h3>
              {data.filtro_produto.length === 0 ? (
                <p className="text-sm text-neutral-500">Sem vendas no período.</p>
              ) : (
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.filtro_produto}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="vendedor_nome" fontSize={11} />
                      <YAxis fontSize={10} />
                      <Tooltip />
                      <Bar dataKey="quantidade" fill="#0A0A0A" name="Unidades" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-neutral-900 p-5">
              <h3 className="font-display font-bold uppercase mb-4">Ranking de Itens</h3>
              {data.ranking_itens.length === 0 ? (
                <p className="text-sm text-neutral-500">Sem dados</p>
              ) : (
                <ol className="space-y-2">
                  {data.ranking_itens.slice(0, 10).map((it, idx) => (
                    <li
                      key={it.produto_id}
                      className="flex items-center gap-3 border-b border-neutral-100 pb-2"
                    >
                      <span className="font-mono font-bold text-sm w-6 text-neutral-500">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 font-display font-bold text-sm">{it.nome}</span>
                      <span className="font-mono text-xs bg-neutral-100 px-2 py-0.5">
                        {it.quantidade} un
                      </span>
                      <span className="font-mono font-bold text-sm">
                        {money(it.faturamento)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="bg-white border-2 border-neutral-900 p-5">
              <h3 className="font-display font-bold uppercase mb-4">Ranking de Vendedores</h3>
              {data.ranking_vendedores.length === 0 ? (
                <p className="text-sm text-neutral-500">Sem dados</p>
              ) : (
                <ol className="space-y-2">
                  {data.ranking_vendedores.slice(0, 10).map((v, idx) => (
                    <li
                      key={v.vendedor_id}
                      className="flex items-center gap-3 border-b border-neutral-100 pb-2"
                    >
                      <span className="font-mono font-bold text-sm w-6 text-neutral-500">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 font-display font-bold text-sm">{v.nome}</span>
                      <span className="font-mono text-xs bg-neutral-100 px-2 py-0.5">
                        {v.itens_vendidos} itens
                      </span>
                      <span className="font-mono text-[10px] text-neutral-500">
                        {v.qtd_vendas}v
                      </span>
                      <span className="font-mono font-bold text-sm">
                        {money(v.faturamento)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </>
      )}

      <DetalheVendasModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        titulo="Performance · Vendas"
        escopo={
          scope === "global"
            ? "GLOBAL"
            : unidades.find((u) => u.id === scope)?.nome || ""
        }
        periodo={
          dataInicio || dataFim
            ? `${dataInicio || "início"} → ${dataFim || "hoje"}`
            : "Todas as datas"
        }
        vendas={vendas}
        metricas={
          data
            ? [
                { label: "FAT BRUTO", value: money(data.faturamento_bruto) },
                { label: "DESCONTO", value: money(data.total_descontos) },
                { label: "FAT LIQ", value: money(data.faturamento_liquido) },
                { label: "CMV", value: money(data.cmv) },
                { label: "LUCRO", value: money(data.lucro) },
                { label: "MARGEM", value: `${data.margem_lucro}%` },
              ]
            : []
        }
      />
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
    {children}
  </label>
);

const Metric = ({ label, value, sub, accent = "bg-neutral-100" }) => (
  <div className="bg-white border-2 border-neutral-900 p-3">
    <div className={`inline-block ${accent} px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest mb-2`}>
      {label}
    </div>
    <div className="font-mono font-bold text-lg">{value}</div>
    {sub && <div className="font-mono text-[10px] text-neutral-500">{sub}</div>}
  </div>
);

const Stat = ({ label, value, sub, accent = "bg-neutral-100" }) => (
  <div className="border-2 border-neutral-900 p-3 bg-white">
    <div className={`inline-block ${accent} px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest mb-2`}>
      {label}
    </div>
    <div className="font-display font-black text-lg">{value}</div>
    {sub && <div className="font-mono text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
  </div>
);
